import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MemberDisplay from "./MemberDisplay";
import styled from "@emotion/styled";
import { Avatar, Box, Chip, Grid, LinearProgress, linearProgressClasses, Typography } from "@mui/material";
import CommonCard from "../../common/components/CommonCard";
import theme from "../../theme";
import CommonButton from "../../common/components/CommonButton";
import { ProjectCategory, ProjectDisplayStatus, ProjectStatus } from "../../common/const";
import moment from "moment";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 6,
    borderRadius: 5,
    marginTop: 8,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.grey[200],
        ...theme.applyStyles('dark', {
            backgroundColor: theme.palette.grey[800],
        }),
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: theme.palette.secondary.main,
        ...theme.applyStyles('dark', {
            backgroundColor: theme.palette.secondary.main,
        }),
    },
}));

const StyledChip = styled(Chip)({
    backgroundColor: '#FAFAFA',
    color: '#4B5563',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '5px',
    height: '23px',
    border: '1px solid #0000001A'
});

const ProjectDisplay = ({
    a, index
}) => {
    const navigate = useNavigate();
    const [previewUrl, setPreviewUrl] = useState(null);
    const [mentorPreviewUrl, setMentorPreviewUrl] = useState(null);

    const getImages = async () => {
        const responseData = await fetch(`${process.env.REACT_APP_BASE_URL}/file?bname=${a.images?.url}&download=false`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        const blob = await responseData.blob();
        const imageUrl = URL.createObjectURL(blob);
        setPreviewUrl(imageUrl || '');
    }

    const getMentorImages = async () => {
        const responseData = await fetch(`${process.env.REACT_APP_BASE_URL}/file?bname=${a.mentor?.user?.avatar}&download=false`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        const blob = await responseData.blob();
        const imageUrl = URL.createObjectURL(blob);
        setMentorPreviewUrl(imageUrl || '');
    }

    useEffect(() => {
        if (a.images?.url) {
            setPreviewUrl(null);
            getImages();
        }
        if (a.mentor?.user?.avatar) {
            setMentorPreviewUrl(null);
            getMentorImages();
        }
    }, [a]);

    return (
        <Grid item md={3} key={index}>
            <CommonCard
                paddingBottom="2px"
                padding="0px"
                borderRadius={1}
                border={`1px solid #0000001A`}
                content={
                    <Box p={2}>
                        <Box sx={{
                            position: 'relative', // Make this a positioned container
                            width: '100%', // Or whatever width you need
                        }}>
                            <img
                                src={previewUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(`${a?.project_name}`)}&background=FAFAFAf1&color=fff`}
                                alt="profile"
                                height="150px"
                                width="100%"
                                style={{ borderRadius: 10 }}
                            />
                        </Box>
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignContent="center">
                                <Typography fontWeight={600} fontSize={15}>{a.project_name}</Typography>
                                {a.status !== ProjectStatus.Enrolled && (
                                    <Box
                                        sx={{
                                            background: a.status === ProjectStatus.InProgress ? theme.palette.secondary.main : a.status === ProjectStatus.Completed ? '#01875F' : '',
                                            padding: '3px 10px',
                                            textAlign: 'center',
                                            marginBottom: 0.5,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography fontSize={13} color={a.status === ProjectStatus.InProgress ? theme.palette.primary.main : a.status === ProjectStatus.Completed ? 'white' : ''}>{ProjectDisplayStatus[a.status]}</Typography>
                                    </Box>
                                )}
                            </Box>
                            {a?.mentor && (
                                <Box display="flex" gap={1} mt={0.5} mb={0.5} alignItems="center">
                                    <Avatar src={mentorPreviewUrl} alt={`${a?.mentor?.user?.first_name} ${a?.mentor?.user?.last_name ? a?.mentor?.user?.last_name : ''}`} sx={{ width: 30, height: 30 }} />
                                    <Typography sx={{ fontSize: 14, color: 'black' }}>
                                        {`${a.mentor?.user?.first_name} ${a.mentor?.user?.last_name}`}
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, mt: 2 }}>
                                {a?.categories?.map((tag, index) => (
                                    <StyledChip key={index} label={ProjectCategory.find((i) => i.value === tag)?.label} size="small" />
                                ))}
                            </Box>
                            {a.status === ProjectStatus.Completed && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography color={theme.palette.background.dark} fontSize={14}>Start Date</Typography>
                                        <Typography fontWeight={600} fontSize={14}>{a.start_date !== null ? moment(a.start_date)?.format('DD MMM, YYYY') : '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography color={theme.palette.background.dark} fontSize={14}>End Date</Typography>
                                        <Typography fontWeight={600} fontSize={14}>{a.end_date !== null ? moment(a.end_date)?.format('DD MMM, YYYY') : '-'}</Typography>
                                    </Grid>
                                </Grid>
                            )}
                            {a.status === ProjectStatus.InProgress && (
                                <Box display="flex" gap={1} mt={0.5} mb={0.5} alignItems="center">
                                    <Typography color={theme.palette.background.dark} fontSize={14}>Due Date:</Typography>
                                    <Typography fontWeight={600} fontSize={14}>{a.end_date !== null ? moment(a.end_date)?.format('DD MMM, YYYY') : '-'}</Typography>
                                </Box>
                            )}
                            <Typography color="background.dark" sx={{ fontSize: 14 }}>
                                {a.description}
                            </Typography>
                        </Box>
                        {a.status === ProjectStatus.InProgress && (
                            <Fragment>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                    <Typography color="background.dark" sx={{ fontSize: 14 }}>Progress</Typography>
                                    <Typography color="background.dark" sx={{ fontSize: 14 }}>{`${a.progress}%`}</Typography>
                                </Box>
                                <BorderLinearProgress variant="determinate" value={a.progress} />
                            </Fragment>
                        )}
                        <Box mt={2} display="flex" justifyContent="space-between">
                            <Box>
                                <MemberDisplay members={a.projectMembers} maxVisible={2} />
                            </Box>
                            <CommonButton
                                label="Open Workspace"
                                width="160px"
                                height="35px"
                                marginBottom='0px'
                                fontWeight="bold"
                                handleClick={() => navigate(`/project-details?id=${a.project_id}`)}
                            />
                        </Box>
                    </Box>
                }
            />
        </Grid>
    )
}

export default ProjectDisplay;